import { validatedDisplayView } from "../../i18n/display-view.js";
import { portalText } from "../../i18n/portal.js";
/**
 * Area `payout` of the console gateway — plan-005 **OP-38** (design §18.8, §18.15.2 IC-52b, IC-59;
 * FR-MNY-5, FR-MNY-6, FR-MNY-8).
 *
 * The shop's side of online payment, as the Payouts screen, the new-agent wizard's "How callers pay"
 * and the voice agent's Payments setting show it:
 *
 *   · **the online-payment status** (`readOnlinePaymentStatus`) — the account's mode, whether callers
 *     are offered online payment now, what stands in the way, and the terms. `fee_note` is a CODE on
 *     the wire; this file owns the words ({@link FEE_NOTE_TEXT});
 *   · **the payable statement** (`readPayoutStatement`, `exportPayoutStatement`) — the balance, held
 *     and available, what holds the next payout, the next payout, the period's entries, refunds,
 *     disputes and payouts, for the live book or (marked "Sandbox") the sandbox book;
 *   · **the payout destination** (`readPayoutDestination`, `setPayoutDestination`) — masked on every
 *     read: the IBAN's last four characters and the tax identifier's last three are all the API ever
 *     returns, and all this file ever holds. The full IBAN is sent once, in the PUT body, and is not
 *     kept, logged or echoed by anything here;
 *   · **the step-up** (`stepUpAuthSession`, IC-59) — `setPayoutDestination` answers `401
 *     step_up_required` when the auth_session has not re-entered its password within a few minutes;
 *     the screen asks for it, calls this, and retries once.
 *
 * Every amount is integer minor units from the wire to `formatMoney` (ADR 0014). Every day is a
 * `YYYY-MM-DD` in Europe/Athens, the time zone every payout cut-off uses.
 *
 * The register is EMPTY: OP-32 and OP-27 put every operation here in the spec before this unit.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";
import {
  CUSTOMER_ORDER_DISPUTE_STATE,
  CUSTOMER_ORDER_REFUND_STATE,
  ONLINE_PAYMENT_MODE,
  PAYABLE_ENTRY_KIND,
  PAYOUT_STATE,
  PROVIDER_MODE,
  money,
  type CustomerOrderDisputeState,
  type CustomerOrderRefundState,
  type MemberRole,
  type Money,
  type OnlinePaymentMode,
  type PayableEntryKind,
  type PayoutState,
  type ProviderMode,
} from "@wetalk/domain";

import type { ConsoleGateway, WorkspaceRoleView } from "../gateway.js";
import { MalformedResponseError, readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap } from "./slice-context.js";

// ---------------------------------------------------------------------------------------------
// Who may do what (OD-P5) — the routes' own role lists, named beside each route
// ---------------------------------------------------------------------------------------------

/** `GET /v1/payouts` and its CSV — Owners, Admins and Viewers (read-only); Members do not. */
export const PAYOUT_STATEMENT_ROLE: readonly MemberRole[] = ["owner", "admin", "viewer"];
/** `GET /v1/payouts/destination` — Owners and Admins, signed in. */
export const PAYOUT_DESTINATION_READ_ROLE: readonly MemberRole[] = ["owner", "admin"];
/** `PUT /v1/payouts/destination` — the Owner, with a fresh step-up. */
export const PAYOUT_DESTINATION_CHANGE_ROLE: readonly MemberRole[] = ["owner"];

export function mayReadPayoutStatement(role: WorkspaceRoleView | null): boolean {
  return role !== null && PAYOUT_STATEMENT_ROLE.includes(role);
}

export function mayReadPayoutDestination(role: WorkspaceRoleView | null): boolean {
  return role !== null && PAYOUT_DESTINATION_READ_ROLE.includes(role);
}

export function mayChangePayoutDestination(role: WorkspaceRoleView | null): boolean {
  return role !== null && PAYOUT_DESTINATION_CHANGE_ROLE.includes(role);
}

// ---------------------------------------------------------------------------------------------
// The online-payment status (FR-MNY-6)
// ---------------------------------------------------------------------------------------------

export const ONLINE_PAYMENT_MISSING = [
  "mode_off",
  "merchant_binding",
  "destination",
  "destination_unverified",
  "general_live",
  "sms_unavailable",
] as const;
export type OnlinePaymentMissingView = (typeof ONLINE_PAYMENT_MISSING)[number];

export const PAYOUT_DESTINATION_STATE = ["missing", "unverified", "verified"] as const;
export type PayoutDestinationStateView = (typeof PAYOUT_DESTINATION_STATE)[number];

export const FEE_NOTE = ["processor_fee_absorbed_no_commission"] as const;
export type FeeNoteView = (typeof FEE_NOTE)[number];

const WEEKDAY = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;
type WeekdayView = (typeof WEEKDAY)[number];

export interface PayoutScheduleView {
  readonly kind: "weekly" | "daily";
  /** The cut-off day of a weekly schedule; `null` for daily. */
  readonly weekday: WeekdayView | null;
  /** Local time of the cut-off, `HH:MM`. */
  readonly cutoffTime: string;
  readonly timeZone: string;
}

export interface OnlinePaymentStatusView {
  readonly mode: OnlinePaymentMode;
  /** A phone caller in a real conversation would be offered online payment now. */
  readonly offeredToCallers: boolean;
  /** A website-widget visitor in a real conversation would be offered it now. */
  readonly offeredInWebChat: boolean;
  /** What stands in the way, in the order someone has to act; empty when nothing does. */
  readonly missing: readonly OnlinePaymentMissingView[];
  readonly destinationState: PayoutDestinationStateView;
  readonly generalLive: boolean;
  readonly feeNote: FeeNoteView;
  readonly payoutSchedule: PayoutScheduleView;
  readonly holdingPeriodDay: number;
  /** The smallest payout. */
  readonly minimum: Money;
  readonly destinationChangeHoldDay: number;
}

/** The mode on a tag. Only WeTalk sets it; the words say what it means for the shop. */
export const ONLINE_PAYMENT_MODE_LABEL: Readonly<Record<OnlinePaymentMode, string>> = validatedDisplayView({
  get off() { return portalText("shell", "not_switched_on_12a2f633"); },
  get sandbox() { return portalText("shell", "test_payments_3966f0f3"); },
  get pilot() { return portalText("shell", "pilot_real_payments_7ce97605"); },
  get "live"() { return portalText("shell", "real_payments_bf51225c"); },
});

export const ONLINE_PAYMENT_MODE_TONE: Readonly<Record<OnlinePaymentMode, "positive" | "caution" | "muted">> = {
  off: "muted",
  sandbox: "caution",
  pilot: "positive",
  "live": "positive",
};

/** Each missing piece in words: what it is and who acts on it. */
export const ONLINE_PAYMENT_MISSING_TEXT: Readonly<Record<OnlinePaymentMissingView, string>> = validatedDisplayView({
  get mode_off() { return portalText("shell", "wetalk_has_not_switched_online_payment_on_for_your_account_626541c6"); },
  get merchant_binding() { return portalText("shell", "wetalk_s_payment_account_is_not_ready_for_your_account_yet_12d648e2"); },
  get destination() { return portalText("shell", "add_your_payout_details_the_bank_account_wetalk_pays_you_i_56d28e7c"); },
  get destination_unverified() { return portalText("shell", "wetalk_is_checking_your_payout_details_fe901b3d"); },
  get general_live() { return portalText("shell", "real_money_payments_are_not_open_to_every_account_yet_14cdd78d"); },
  get sms_unavailable() { return portalText("shell", "text_messages_cannot_be_sent_yet_so_phone_callers_cannot_r_1b13e0b9"); },
});

/** `fee_note` is a code on the wire; these are its words. */
export const FEE_NOTE_TEXT: Readonly<Record<FeeNoteView, string>> = validatedDisplayView({
  get processor_fee_absorbed_no_commission() { return portalText("shell", "wetalk_absorbs_the_processing_fee_and_takes_no_commission_fd23b632"); },
});

const WEEKDAY_LABEL = () => ({
  monday: portalText("shell", "monday_932eeb10"),
  tuesday: portalText("shell", "tuesday_42e43b61"),
  wednesday: portalText("shell", "wednesday_5656b9b7"),
  thursday: portalText("shell", "thursday_76031ddf"),
  friday: portalText("shell", "friday_d166e844"),
  saturday: portalText("shell", "saturday_17063c50"),
  sunday: portalText("shell", "sunday_bc5dd045"),
});

/** "every Monday at 00:00, Athens time" — the schedule as a shop reads it. */
export function payoutScheduleText(schedule: PayoutScheduleView): string {
  const zone = schedule.timeZone === "Europe/Athens" ? portalText("shell", "athens_time_f7dc7870") : schedule.timeZone;
  if (schedule.kind === "daily" || schedule.weekday === null) return portalText("shell", "every_day_at_value_value_6b80e707", { v0: String(schedule.cutoffTime), v1: String(zone) });
  return portalText("shell", "every_value_at_value_value_10a18da4", { v0: String(WEEKDAY_LABEL()[schedule.weekday]), v1: String(schedule.cutoffTime), v2: String(zone) });
}

function dayCount(day: number): string {
  return day === 1 ? portalText("shell", "1_day_2d8a6f7c") : portalText("shell", "value_days_e2bb1259", { v0: String(String(day)) });
}

/**
 * The collect-and-pay-out terms, one sentence each, in the order a shop needs them (plan-005 OP-38
 * Task 2): who bears the fee, when money is paid out, the minimum, and the destination-change hold.
 */
export function onlinePaymentTermsOf(status: OnlinePaymentStatusView, formatAmount: (amount: Money) => string): readonly string[] {
  return [
    FEE_NOTE_TEXT[status.feeNote],
    portalText("shell", "callers_pay_wetalk_and_wetalk_pays_you_each_payment_is_hel_a47da7a4", { v0: String(dayCount(status.holdingPeriodDay)), v1: String(payoutScheduleText(status.payoutSchedule)) }),
    portalText("shell", "a_payout_is_at_least_value_a_smaller_balance_waits_for_the_f8523e43", { v0: String(formatAmount(status.minimum)) }),
    portalText("shell", "changing_your_payout_details_holds_payouts_for_value_while_5eb73e50", { v0: String(dayCount(status.destinationChangeHoldDay)) }),
  ];
}

/**
 * The status in one sentence: whether callers are offered online payment now. When they are not,
 * the missing pieces say why ({@link ONLINE_PAYMENT_MISSING_TEXT}).
 */
export function onlinePaymentHeadlineOf(status: OnlinePaymentStatusView): string {
  if (status.offeredToCallers && status.offeredInWebChat) {
    return status.mode === "sandbox"
      ? portalText("shell", "callers_are_offered_online_payment_now_as_test_payments_no_e51e4a5e")
      : portalText("shell", "callers_are_offered_online_payment_now_d11722ff");
  }
  if (status.offeredInWebChat) return portalText("shell", "website_visitors_are_offered_online_payment_now_phone_call_0f49e0ae");
  if (status.offeredToCallers) return portalText("shell", "phone_callers_are_offered_online_payment_now_website_visit_8e99a500");
  return portalText("shell", "callers_are_not_offered_online_payment_yet_until_they_are_1eb26ca9");
}

// ---------------------------------------------------------------------------------------------
// The statement (FR-MNY-8)
// ---------------------------------------------------------------------------------------------

export const PAYOUT_HOLD_REASON = [
  "paused",
  "reconciliation",
  "destination_missing",
  "destination_unverified",
  "destination_change",
  "negative_balance",
  "below_minimum",
] as const;
export type PayoutHoldReasonView = (typeof PAYOUT_HOLD_REASON)[number];

export interface PayoutHoldView {
  readonly reason: PayoutHoldReasonView;
  /** When the hold ends by itself (`destination_change` only). */
  readonly until: Date | null;
}

export interface PayableHeldCreditView {
  readonly payableEntryId: string;
  readonly customerOrderReference: string | null;
  readonly amount: Money;
  readonly releaseAt: Date;
}

export interface PayoutExpectedView {
  readonly cutoffAt: Date;
  /** What a payout could take at the cut-off, credits released by then included. */
  readonly available: Money;
  /** `available` when nothing holds the payout; otherwise zero. */
  readonly expected: Money;
  readonly minimum: Money;
}

export interface PayableStatementEntryView {
  readonly payableEntryId: string;
  readonly sequence: number;
  readonly kind: PayableEntryKind;
  readonly occurredAt: Date;
  readonly availableAt: Date;
  /** Signed: positive means WeTalk owes more. */
  readonly amount: Money;
  readonly balanceAfter: Money;
  readonly customerOrderReference: string | null;
  /** An order line's gross; `null` for a payout or an adjustment. */
  readonly gross: Money | null;
  /** The processor fee WeTalk absorbed on the line — WeTalk's, never the shop's. */
  readonly weTalkFeeAbsorbed: Money | null;
  /** Always zero on an order line. */
  readonly commission: Money | null;
  /** Equals `gross` on an order line. */
  readonly net: Money | null;
  readonly payoutId: string | null;
  readonly reason: string | null;
}

export interface PayableStatementRefundView {
  readonly customerOrderRefundId: string;
  readonly customerOrderReference: string;
  readonly amount: Money;
  readonly state: CustomerOrderRefundState;
  readonly reason: string;
  readonly createdAt: Date;
  readonly resolvedAt: Date | null;
}

export interface PayableStatementDisputeView {
  readonly customerOrderDisputeId: string;
  readonly customerOrderReference: string;
  readonly amount: Money;
  readonly state: CustomerOrderDisputeState;
  readonly detectedAt: Date;
  readonly resolvedAt: Date | null;
}

export interface PayoutStatementPayoutView {
  readonly payoutId: string;
  readonly amount: Money;
  readonly state: PayoutState;
  readonly cutoffAt: Date;
  readonly createdAt: Date;
  readonly sentAt: Date | null;
  readonly settledAt: Date | null;
  readonly failedAt: Date | null;
  readonly cancelledAt: Date | null;
  readonly bankReference: string | null;
  readonly failureReason: string | null;
  /** Where it went, as it was — never the full IBAN. */
  readonly ibanLastFour: string | null;
  readonly beneficiaryName: string | null;
}

export interface PayoutStatementView {
  readonly providerMode: ProviderMode;
  readonly period: { readonly from: string; readonly to: string };
  readonly asOf: Date;
  readonly balance: Money;
  readonly held: Money;
  /** `balance − held`; negative after refunds or disputes followed a payout (carried, never paid out). */
  readonly available: Money;
  readonly heldCredit: readonly PayableHeldCreditView[];
  readonly hold: readonly PayoutHoldView[];
  readonly nextPayout: PayoutExpectedView | null;
  readonly openingBalance: Money;
  readonly closingBalance: Money;
  readonly entryTotal: Money;
  readonly entry: readonly PayableStatementEntryView[];
  readonly refund: readonly PayableStatementRefundView[];
  readonly dispute: readonly PayableStatementDisputeView[];
  readonly payout: readonly PayoutStatementPayoutView[];
  /** The console offers its "Sandbox" view only when the sandbox book has an entry. */
  readonly sandboxBookHasEntries: boolean;
}

export interface PayoutStatementQueryView {
  /** `YYYY-MM-DD`, Europe/Athens. */
  readonly from: string;
  /** `YYYY-MM-DD`, inclusive; at most 366 days after `from`. */
  readonly to: string;
  readonly providerMode: ProviderMode;
}

/** What holds the next payout, in words. `minimum` is the schedule's, for `below_minimum`. */
export function payoutHoldText(hold: PayoutHoldView, minimumText: string, formatWhen: (at: Date) => string): string {
  switch (hold.reason) {
    case "paused":
      return portalText("shell", "wetalk_has_paused_payouts_for_your_account_our_team_will_c_f7b5df4e");
    case "reconciliation":
      return portalText("shell", "wetalk_is_reconciling_a_payment_payouts_wait_until_it_is_s_68839e73");
    case "destination_missing":
      return portalText("shell", "add_your_payout_details_to_receive_payouts_a0c62df8");
    case "destination_unverified":
      return portalText("shell", "wetalk_is_checking_your_payout_details_payouts_start_once_a904a3c4");
    case "destination_change":
      return hold.until === null
        ? portalText("shell", "your_payout_details_changed_recently_so_payouts_wait_while_4ce518d8")
        : portalText("shell", "your_payout_details_changed_recently_so_payouts_wait_until_9e61ec2f", { v0: String(formatWhen(hold.until)) });
    case "negative_balance":
      return portalText("shell", "refunds_or_disputes_after_the_last_payout_left_the_balance_15baa105");
    case "below_minimum":
      return portalText("shell", "the_amount_available_is_below_the_value_minimum_it_waits_f_4438b0a2", { v0: String(minimumText) });
  }
}

export const PAYABLE_ENTRY_KIND_LABEL: Readonly<Record<PayableEntryKind, string>> = validatedDisplayView({
  get payment_captured() { return portalText("shell", "order_paid_online_91f7e1d6"); },
  get refund() { return portalText("shell", "refund_to_the_payer_d337e8d3"); },
  get refund_returned() { return portalText("shell", "refund_returned_4faf2c8e"); },
  get dispute_opened() { return portalText("shell", "payment_disputed_7a1aa576"); },
  get dispute_reversed() { return portalText("shell", "dispute_decided_for_you_cc2e3001"); },
  get payout() { return portalText("shell", "payout_to_your_bank_af8e303f"); },
  get payout_returned() { return portalText("shell", "payout_returned_49e75f4c"); },
  get adjustment() { return portalText("shell", "adjustment_by_wetalk_7b81c034"); },
  get processor_fee() { return portalText("shell", "processing_fee_f97e9e5c"); },
  get commission() { return portalText("shell", "commission_4bfc46bc"); },
});

export const PAYOUT_STATE_LABEL: Readonly<Record<PayoutState, string>> = validatedDisplayView({
  get created() { return portalText("shell", "being_prepared_08f2c1ae"); },
  get approved() { return portalText("shell", "approved_41b81eb8"); },
  get sent() { return portalText("shell", "sent_to_your_bank_34260efd"); },
  get settled() { return portalText("shell", "paid_dc9d4584"); },
  get failed() { return portalText("shell", "failed_09fef5d8"); },
  get cancelled() { return portalText("shell", "cancelled_a1bf92ef"); },
});

export const PAYOUT_STATE_TONE: Readonly<Record<PayoutState, "positive" | "caution" | "critical" | "muted">> = {
  created: "muted",
  approved: "caution",
  sent: "caution",
  settled: "positive",
  failed: "critical",
  cancelled: "muted",
};

export const REFUND_STATE_LABEL: Readonly<Record<CustomerOrderRefundState, string>> = validatedDisplayView({
  get created() { return portalText("shell", "requested_c26bf60f"); },
  get sent() { return portalText("shell", "sent_35f49dcf"); },
  get ambiguous() { return portalText("shell", "being_confirmed_f0ecc631"); },
  get succeeded() { return portalText("shell", "refunded_d6fbc7a8"); },
  get failed() { return portalText("shell", "failed_09fef5d8"); },
});

export const DISPUTE_STATE_LABEL: Readonly<Record<CustomerOrderDisputeState, string>> = validatedDisplayView({
  get opened() { return portalText("shell", "open_cf9b7706"); },
  get won() { return portalText("shell", "decided_for_you_fe32d98b"); },
  get lost() { return portalText("shell", "decided_for_the_payer_c33374b3"); },
});

// ---------------------------------------------------------------------------------------------
// The destination (FR-MNY-5)
// ---------------------------------------------------------------------------------------------

export interface PayoutDestinationView {
  readonly payoutDestinationId: string;
  readonly beneficiaryName: string;
  /** The IBAN's last four characters — the only part of it the console ever receives. */
  readonly ibanLastFour: string;
  readonly countryCode: string;
  readonly bic: string | null;
  readonly taxIdentifierLastThree: string;
  readonly verified: boolean;
  readonly verifiedAt: Date | null;
  /** Payouts wait until this instant after a change. */
  readonly holdUntil: Date | null;
  readonly createdAt: Date;
}

/** The PUT body. The IBAN is sent once and never read back. */
export interface PayoutDestinationChangeView {
  readonly beneficiaryName: string;
  readonly iban: string;
  /** Required only for a SEPA country outside the EEA; `null` otherwise. */
  readonly bic: string | null;
  readonly taxIdentifier: string;
}

/** `•••• 1234` — the masked IBAN as the design shows it. */
export function maskedIbanText(ibanLastFour: string): string {
  return `•••• ${ibanLastFour}`;
}

// ---------------------------------------------------------------------------------------------
// Decoding
// ---------------------------------------------------------------------------------------------

function oneOf<const T extends readonly string[]>(allowed: T, raw: unknown, at: string, expected: string): T[number] {
  const value = readText(raw, at);
  const found = allowed.find((one) => one === value);
  if (found === undefined) throw new MalformedResponseError(at, expected, value);
  return found;
}

function instantOf(raw: unknown, at: string): Date {
  const when = new Date(readText(raw, at));
  if (Number.isNaN(when.getTime())) throw new MalformedResponseError(at, "an RFC 3339 timestamp", raw);
  return when;
}

function nullableInstantOf(raw: unknown, at: string): Date | null {
  return raw === null ? null : instantOf(raw, at);
}

function nullableTextOf(raw: unknown, at: string): string | null {
  return raw === null ? null : readText(raw, at);
}

function eur(raw: unknown, at: string): Money {
  const minor = readWholeNumber(raw, at);
  if (!Number.isSafeInteger(minor)) throw new MalformedResponseError(at, "integer minor units", raw);
  return money(minor, "EUR");
}

function nullableEur(raw: unknown, at: string): Money | null {
  return raw === null ? null : eur(raw, at);
}

function dataOf(body: unknown, operationId: string): unknown {
  return readRecord(body, operationId)["data"];
}

export function onlinePaymentStatusOfWire(body: unknown, operationId = "readOnlinePaymentStatus"): OnlinePaymentStatusView {
  const at = `${operationId}.data`;
  const data = readRecord(dataOf(body, operationId), at);
  const schedule = readRecord(data["payout_schedule"], `${at}.payout_schedule`);
  const weekday = schedule["weekday"];
  return {
    mode: oneOf(ONLINE_PAYMENT_MODE, data["mode"], `${at}.mode`, portalText("shell", "an_online_payment_mode_5cdb0374")),
    offeredToCallers: readBoolean(data["offered_to_callers"], `${at}.offered_to_callers`),
    offeredInWebChat: readBoolean(data["offered_in_web_chat"], `${at}.offered_in_web_chat`),
    missing: readArray(data["missing"], `${at}.missing`).map((one, index) =>
      oneOf(ONLINE_PAYMENT_MISSING, one, `${at}.missing[${index}]`, portalText("shell", "a_documented_missing_piece_ca8e05cf")),
    ),
    destinationState: oneOf(PAYOUT_DESTINATION_STATE, data["destination_state"], `${at}.destination_state`, portalText("shell", "a_destination_state_55842d99")),
    generalLive: readBoolean(data["general_live"], `${at}.general_live`),
    feeNote: oneOf(FEE_NOTE, data["fee_note"], `${at}.fee_note`, portalText("shell", "a_documented_fee_note_33234a16")),
    payoutSchedule: {
      kind: oneOf(["weekly", "daily"] as const, schedule["kind"], `${at}.payout_schedule.kind`, portalText("shell", "weekly_or_daily_f53d8c93")),
      weekday: weekday === null ? null : oneOf(WEEKDAY, weekday, `${at}.payout_schedule.weekday`, portalText("shell", "a_weekday_bc8603dc")),
      cutoffTime: readText(schedule["cutoff_time"], `${at}.payout_schedule.cutoff_time`),
      timeZone: readText(schedule["time_zone"], `${at}.payout_schedule.time_zone`),
    },
    holdingPeriodDay: readWholeNumber(data["holding_period_day"], `${at}.holding_period_day`),
    minimum: eur(data["minimum_minor"], `${at}.minimum_minor`),
    destinationChangeHoldDay: readWholeNumber(data["destination_change_hold_day"], `${at}.destination_change_hold_day`),
  };
}

export function payoutStatementOfWire(body: unknown, operationId = "readPayoutStatement"): PayoutStatementView {
  const at = `${operationId}.data`;
  const data = readRecord(dataOf(body, operationId), at);
  const currency = readText(data["currency"], `${at}.currency`);
  if (currency !== "EUR") throw new MalformedResponseError(`${at}.currency`, "EUR, which is what this console renders", currency);
  const period = readRecord(data["period"], `${at}.period`);
  const balance = readRecord(data["balance"], `${at}.balance`);
  const next = data["next_payout"];
  return {
    providerMode: oneOf(PROVIDER_MODE, data["provider_mode"], `${at}.provider_mode`, portalText("shell", "sandbox_or_live_9e0b5526")),
    period: { from: readText(period["from"], `${at}.period.from`), to: readText(period["to"], `${at}.period.to`) },
    asOf: instantOf(data["as_of"], `${at}.as_of`),
    balance: eur(balance["balance_minor"], `${at}.balance.balance_minor`),
    held: eur(balance["held_minor"], `${at}.balance.held_minor`),
    available: eur(balance["available_minor"], `${at}.balance.available_minor`),
    heldCredit: readArray(data["held_credits"], `${at}.held_credits`).map((raw, index) => {
      const path = `${at}.held_credits[${index}]`;
      const one = readRecord(raw, path);
      return {
        payableEntryId: readText(one["payable_entry_id"], `${path}.payable_entry_id`),
        customerOrderReference: nullableTextOf(one["customer_order_reference"], `${path}.customer_order_reference`),
        amount: eur(one["amount_minor"], `${path}.amount_minor`),
        releaseAt: instantOf(one["release_at"], `${path}.release_at`),
      };
    }),
    hold: readArray(data["holds"], `${at}.holds`).map((raw, index) => {
      const path = `${at}.holds[${index}]`;
      const one = readRecord(raw, path);
      return {
        reason: oneOf(PAYOUT_HOLD_REASON, one["reason"], `${path}.reason`, portalText("shell", "a_documented_hold_reason_b3163d9a")),
        until: nullableInstantOf(one["until"], `${path}.until`),
      };
    }),
    nextPayout:
      next === null
        ? null
        : (() => {
            const path = `${at}.next_payout`;
            const one = readRecord(next, path);
            return {
              cutoffAt: instantOf(one["cutoff_at"], `${path}.cutoff_at`),
              available: eur(one["available_minor"], `${path}.available_minor`),
              expected: eur(one["expected_amount_minor"], `${path}.expected_amount_minor`),
              minimum: eur(one["minimum_minor"], `${path}.minimum_minor`),
            };
          })(),
    openingBalance: eur(data["opening_balance_minor"], `${at}.opening_balance_minor`),
    closingBalance: eur(data["closing_balance_minor"], `${at}.closing_balance_minor`),
    entryTotal: eur(data["entry_total_minor"], `${at}.entry_total_minor`),
    entry: readArray(data["entries"], `${at}.entries`).map((raw, index) => {
      const path = `${at}.entries[${index}]`;
      const one = readRecord(raw, path);
      return {
        payableEntryId: readText(one["payable_entry_id"], `${path}.payable_entry_id`),
        sequence: readWholeNumber(one["sequence"], `${path}.sequence`),
        kind: oneOf(PAYABLE_ENTRY_KIND, one["kind"], `${path}.kind`, portalText("shell", "a_documented_entry_kind_580b5eff")),
        occurredAt: instantOf(one["occurred_at"], `${path}.occurred_at`),
        availableAt: instantOf(one["available_at"], `${path}.available_at`),
        amount: eur(one["amount_minor"], `${path}.amount_minor`),
        balanceAfter: eur(one["balance_after_minor"], `${path}.balance_after_minor`),
        customerOrderReference: nullableTextOf(one["customer_order_reference"], `${path}.customer_order_reference`),
        gross: nullableEur(one["gross_minor"], `${path}.gross_minor`),
        weTalkFeeAbsorbed: nullableEur(one["wetalk_fee_absorbed_minor"], `${path}.wetalk_fee_absorbed_minor`),
        commission: nullableEur(one["commission_minor"], `${path}.commission_minor`),
        net: nullableEur(one["net_minor"], `${path}.net_minor`),
        payoutId: nullableTextOf(one["payout_id"], `${path}.payout_id`),
        reason: nullableTextOf(one["reason"], `${path}.reason`),
      };
    }),
    refund: readArray(data["refunds"], `${at}.refunds`).map((raw, index) => {
      const path = `${at}.refunds[${index}]`;
      const one = readRecord(raw, path);
      return {
        customerOrderRefundId: readText(one["customer_order_refund_id"], `${path}.customer_order_refund_id`),
        customerOrderReference: readText(one["customer_order_reference"], `${path}.customer_order_reference`),
        amount: eur(one["amount_minor"], `${path}.amount_minor`),
        state: oneOf(CUSTOMER_ORDER_REFUND_STATE, one["state"], `${path}.state`, portalText("shell", "a_refund_state_36dd1bc5")),
        reason: readText(one["reason"], `${path}.reason`),
        createdAt: instantOf(one["created_at"], `${path}.created_at`),
        resolvedAt: nullableInstantOf(one["resolved_at"], `${path}.resolved_at`),
      };
    }),
    dispute: readArray(data["disputes"], `${at}.disputes`).map((raw, index) => {
      const path = `${at}.disputes[${index}]`;
      const one = readRecord(raw, path);
      return {
        customerOrderDisputeId: readText(one["customer_order_dispute_id"], `${path}.customer_order_dispute_id`),
        customerOrderReference: readText(one["customer_order_reference"], `${path}.customer_order_reference`),
        amount: eur(one["amount_minor"], `${path}.amount_minor`),
        state: oneOf(CUSTOMER_ORDER_DISPUTE_STATE, one["state"], `${path}.state`, portalText("shell", "a_dispute_state_c12d2ec9")),
        detectedAt: instantOf(one["detected_at"], `${path}.detected_at`),
        resolvedAt: nullableInstantOf(one["resolved_at"], `${path}.resolved_at`),
      };
    }),
    payout: readArray(data["payouts"], `${at}.payouts`).map((raw, index) => {
      const path = `${at}.payouts[${index}]`;
      const one = readRecord(raw, path);
      const destination = readRecord(one["destination"], `${path}.destination`);
      return {
        payoutId: readText(one["payout_id"], `${path}.payout_id`),
        amount: eur(one["amount_minor"], `${path}.amount_minor`),
        state: oneOf(PAYOUT_STATE, one["state"], `${path}.state`, portalText("shell", "a_payout_state_9154ae14")),
        cutoffAt: instantOf(one["cutoff_at"], `${path}.cutoff_at`),
        createdAt: instantOf(one["created_at"], `${path}.created_at`),
        sentAt: nullableInstantOf(one["sent_at"], `${path}.sent_at`),
        settledAt: nullableInstantOf(one["settled_at"], `${path}.settled_at`),
        failedAt: nullableInstantOf(one["failed_at"], `${path}.failed_at`),
        cancelledAt: nullableInstantOf(one["cancelled_at"], `${path}.cancelled_at`),
        bankReference: nullableTextOf(one["bank_reference"], `${path}.bank_reference`),
        failureReason: nullableTextOf(one["failure_reason"], `${path}.failure_reason`),
        ibanLastFour: nullableTextOf(destination["iban_last_four"], `${path}.destination.iban_last_four`),
        beneficiaryName: nullableTextOf(destination["beneficiary_name"], `${path}.destination.beneficiary_name`),
      };
    }),
    sandboxBookHasEntries: readBoolean(data["sandbox_book_has_entries"], `${at}.sandbox_book_has_entries`),
  };
}

/** One destination, or `null` until the Owner adds one. Reads only the masked fields. */
export function payoutDestinationOfWire(body: unknown, operationId: string): PayoutDestinationView | null {
  const raw = dataOf(body, operationId);
  if (raw === null) return null;
  const at = `${operationId}.data`;
  const one = readRecord(raw, at);
  return {
    payoutDestinationId: readText(one["payout_destination_id"], `${at}.payout_destination_id`),
    beneficiaryName: readText(one["beneficiary_name"], `${at}.beneficiary_name`),
    ibanLastFour: readText(one["iban_last_four"], `${at}.iban_last_four`),
    countryCode: readText(one["country_code"], `${at}.country_code`),
    bic: nullableTextOf(one["bic"], `${at}.bic`),
    taxIdentifierLastThree: readText(one["tax_identifier_last_three"], `${at}.tax_identifier_last_three`),
    verified: readBoolean(one["verified"], `${at}.verified`),
    verifiedAt: nullableInstantOf(one["verified_at"], `${at}.verified_at`),
    holdUntil: nullableInstantOf(one["hold_until"], `${at}.hold_until`),
    createdAt: instantOf(one["created_at"], `${at}.created_at`),
  };
}

// ---------------------------------------------------------------------------------------------
// The gateway
// ---------------------------------------------------------------------------------------------

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** Whether callers are offered online payment now, what is missing, and the terms (FR-MNY-6). */
    readOnlinePaymentStatus(): Promise<OnlinePaymentStatusView>;
    /** The payable statement for one book and one period (FR-MNY-8). */
    readPayoutStatement(query: PayoutStatementQueryView): Promise<PayoutStatementView>;
    /** The same period as a CSV document (UTF-8 with a BOM), built when asked and never stored. */
    exportPayoutStatement(query: PayoutStatementQueryView): Promise<string>;
    /** Where payouts go, masked; `null` until the Owner adds one. Owners and Admins. */
    readPayoutDestination(): Promise<PayoutDestinationView | null>;
    /**
     * Changes where payouts go — the Owner, within a few minutes of {@link stepUpAuthSession}.
     * Raises `ApiError` `step_up_required` (401) when the step-up is missing or stale.
     */
    setPayoutDestination(change: PayoutDestinationChangeView): Promise<PayoutDestinationView>;
    /** Re-enters the signed-in person's password so a money change is allowed for a few minutes. */
    stepUpAuthSession(password: string): Promise<void>;
  }
}

export type PayoutGatewaySlice = Pick<
  ConsoleGateway,
  | "readOnlinePaymentStatus"
  | "readPayoutStatement"
  | "exportPayoutStatement"
  | "readPayoutDestination"
  | "setPayoutDestination"
  | "stepUpAuthSession"
>;

/** Empty: OP-32 and OP-27 documented every operation this slice calls. */
export const payoutGap: readonly GatewayGap[] = [];

function statementQueryOf(query: PayoutStatementQueryView): { from: string; to: string; provider_mode: string } {
  return { from: query.from, to: query.to, provider_mode: query.providerMode };
}

export function payoutApiSlice(client: WeTalkApiClient): PayoutGatewaySlice {
  return {
    async readOnlinePaymentStatus() {
      return onlinePaymentStatusOfWire(assertOk(await client.readOnlinePaymentStatus()));
    },
    async readPayoutStatement(query) {
      return payoutStatementOfWire(assertOk(await client.readPayoutStatement({ query: statementQueryOf(query) })));
    },
    async exportPayoutStatement(query) {
      // The generated client reads a body that is not JSON as text: the CSV itself.
      const answered = assertOk(await client.exportPayoutStatement({ query: statementQueryOf(query) }));
      if (typeof answered !== "string") {
        throw new MalformedResponseError("exportPayoutStatement", "a CSV document", answered);
      }
      return answered;
    },
    async readPayoutDestination() {
      return payoutDestinationOfWire(assertOk(await client.readPayoutDestination()), "readPayoutDestination");
    },
    async setPayoutDestination(change) {
      const answered = assertOk(
        await client.setPayoutDestination({
          body: {
            beneficiary_name: change.beneficiaryName,
            iban: change.iban,
            bic: change.bic,
            tax_identifier: change.taxIdentifier,
          },
        }),
      );
      const destination = payoutDestinationOfWire(answered, "setPayoutDestination");
      if (destination === null) {
        throw new MalformedResponseError("setPayoutDestination.data", "the new destination", null);
      }
      return destination;
    },
    async stepUpAuthSession(password) {
      assertOk(await client.stepUpAuthSession({ body: { factor: "password", password } }));
    },
  };
}
